/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/control-has-associated-label */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
  CmdInput,
  CmdLabel,
  CmdSimpleSelect,
  CmdSimpleSelectOption,
  CmdSubpage,
  CmdSubpageButton,
  CmdTextarea,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import Section from '../../Forms/Section';
import 'react-grid-layout/css/styles.css';
import WidgetCategory from './WidgetCategory';
import Switch from '../../Forms/ReactHookFormFields/Switch';
import FormikValidatorService, {
  IValidationApiResponse,
  IValidationRule,
} from '../../../Services/FormikValidatorService';
import Widget from '../Widgets/Widget';
import WidgetType from './WidgetType';
import CardsSection from './CardsSection';
import {
  createTemplate,
  getAvailableWidgetsByType,
  getInitialFormData,
  getTemplateData,
  getTemplateSizeByCards,
  getValidation,
  getWidgetCategories,
  getWidgetTypes,
  getWidgetTypesByCategory,
  IS_PUBLIC,
  loadWidgetsFromLocalStorage,
  MAX_GRID_COLS,
  MAX_GRID_ROWS,
  removeWidgetsFromLocalStorage,
  showGridAndWidgets,
  updateTemplate,
  validateWidgetsInGrid,
} from '../OnlineTemplateService';
import PermissionsService, {
  PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../../Services/PermissionsService';
import usePermissions from '../../../hooks/usePermissions';
import { transformErrors } from '../../../Services/HookFormService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  CardError,
  CardItem,
  GridLayoutDroppingItem,
  OnlineTemplateData,
  OnlineTemplateFormValues,
} from './types';
import { IWidget, IWidgetCategory, IWidgetType } from '../Widgets/types';
import {
  getWidgetCategoryNameById,
  getWidgets,
  getWidgetTypeNameById,
} from '../Widgets/WidgetService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IAddTemplateProps {
  closeCallback: (value?: string) => void;
  id: number | null;
  reloadTableCallback: () => void;
}

export default function TemplateForm(props: IAddTemplateProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const intl = useIntl();
  const { formatMessage: f } = intl;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [activeWidgetCategory, setActiveWidgetCategory] = React.useState<string>('');
  const [activeWidgetType, setActiveWidgetType] = React.useState<string>('');

  const [draggedWidget, setDraggedWidget] = React.useState<string | null>(null);
  const [droppingWidget, setDroppingWidget] = React.useState<GridLayoutDroppingItem | undefined>(
    undefined
  );
  // const [onlineTemplateTypes, setOnlineTemplateTypes] = React.useState<CmdSimpleSelectOption[]>([]);
  // const [onlineTemplateCustomers, setOnlineTemplateCustomers] = React.useState<
  //   CmdSimpleSelectOption[]
  // >([]);
  // const [allCustomers, setAllCustomers] = React.useState<CmdSimpleSelectOption[]>([]);
  // const [onlineTemplateWidgets, setOnlineTemplateWidgets] = React.useState<IWidget[]>([]);
  // const [onlineTemplateWidgetCategories, setOnlineTemplateWidgetCategories] = React.useState<
  //   IWidgetCategory[]
  // >([]);
  // const [onlineTemplateWidgetTypes, setOnlineTemplateWidgetTypes] = React.useState<IWidgetType[]>(
  //   []
  // );
  // const [onlineTemplateValidation, setOnlineTemplateValidation] =
  //   React.useState<IValidationApiResponse | null>(null);
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = React.useState(false);

  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  const access = usePermissions(
    [
      PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
      PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
      PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
      PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
    ],
    selectedCustomers
  );

  const { data: validationData, isLoading: isLoadingValidationData } = useQuery({
    queryKey: ['onlineTemplateValidation'],
    queryFn: () => getValidation(),
  });

  const { data: initialFormData, isLoading: isLoadingInitialFormData } = useQuery({
    queryKey: ['onlineTemplateInitialFormData'],
    queryFn: () => getInitialFormData(),
  });

  const { data: widgetCategoriesData, isLoading: isLoadingWidgetCategoriesData } = useQuery({
    queryKey: ['onlineTemplateWidgetCategories'],
    queryFn: () => getWidgetCategories(),
  });

  const { data: widgetTypesData, isLoading: isLoadingWidgetTypesData } = useQuery({
    queryKey: ['onlineTemplateWidgetTypes'],
    queryFn: () => getWidgetTypes(),
  });

  const { data: widgetsData, isLoading: isLoadingWidgetsData } = useQuery({
    queryKey: ['onlineTemplateWidgets'],
    queryFn: () => getWidgets() as Promise<IWidget[]>,
  });

  const { data: onlinePanelData, isLoading: isLoadingOnlinePanelData } = useQuery({
    queryKey: ['onlinePanelData'],
    queryFn: () => getTemplateData(props.id),
    enabled: !!(
      props.id &&
      widgetsData &&
      widgetTypesData &&
      widgetCategoriesData &&
      validationData
    ),
  });

  const validateCardName = (card: CardItem, index: number, formErrors) => {
    const clonedErrors = { ...formErrors };
    if (!card.name.trim()) {
      if (!clonedErrors.cards) {
        clonedErrors.cards = {};
      }

      if (!clonedErrors.cards[index]) {
        clonedErrors.cards[index] = {};
      }

      clonedErrors.cards[index].name = f({ id: 'form.required' });
    }
    return clonedErrors;
  };

  const validateFields = (values: OnlineTemplateFormValues) => {
    let formErrors: any = {};
    if (validationData) {
      formErrors = FormikValidatorService.validateByValidationApiResponse(values, validationData);
    }
    formErrors = transformErrors(formErrors);

    if (Array.isArray(values.cards) && validationData) {
      formErrors.cards = {};
      const widgetsInLocalStorage = loadWidgetsFromLocalStorage();

      if (widgetsInLocalStorage) {
        values.cards.map((card: CardItem, index: number) => {
          formErrors = validateCardName(card, index, formErrors); // Validate the name of the card

          const cardErrors = FormikValidatorService.validateByValidationApiResponse(
            widgetsInLocalStorage[index] ? widgetsInLocalStorage[index] : [],
            validationData
          );
          if (Object.keys(cardErrors).length > 0) {
            (formErrors.cards as Record<string, unknown>[])[index] = cardErrors;
          }
          const gridErrorsData = validateWidgetsInGrid(
            widgetsInLocalStorage[index] ? widgetsInLocalStorage[index] : [],
            MAX_GRID_COLS,
            MAX_GRID_ROWS,
            intl
          );
          if (gridErrorsData) {
            (formErrors.cards as Record<string, unknown>[])[index] = gridErrorsData;
          }
        });
      } else {
        values.cards.map((card: CardItem, index: number) => {
          formErrors = validateCardName(card, index, formErrors); // Validate the name of the card

          const cardErrors = FormikValidatorService.validateByValidationApiResponse(
            card,
            validationData
          );
          if (Object.keys(cardErrors).length > 0) {
            (formErrors.cards as Record<string, unknown>[])[index] = cardErrors;
          }
          const gridErrorsData = validateWidgetsInGrid(
            card.widgets,
            MAX_GRID_COLS,
            MAX_GRID_ROWS,
            intl
          );
          if (gridErrorsData) {
            (formErrors.cards as Record<string, unknown>[])[index] = gridErrorsData;
          }
        });
      }
      if (Object.keys(formErrors.cards).length === 0) {
        delete formErrors.cards;
      }
    }
    return formErrors;
  };

  const allCustomers = React.useMemo(() => {
    let customers: CmdSimpleSelectOption[] = [];
    if (initialFormData?.customerId) {
      customers = initialFormData.customerId;
    }
    return customers;
  }, [initialFormData?.customerId]);

  const enabledTemplateTypes = React.useMemo<CmdSimpleSelectOption[]>(() => {
    const templateTypes: CmdSimpleSelectOption[] = [];
    if (initialFormData?.type) {
      initialFormData.type.forEach((templateType: CmdSimpleSelectOption) => {
        if (
          templateType.value === 'system' &&
          PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM, ['create'])
        ) {
          templateTypes.push(templateType);
        }
        if (
          templateType.value === 'public' &&
          PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER, ['create'])
        ) {
          templateTypes.push(templateType);
        }
        if (
          templateType.value === 'private' &&
          PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE, ['create'])
        ) {
          templateTypes.push(templateType);
        }
      });
    }
    return templateTypes;
  }, [initialFormData]);

  const enabledCustomers = React.useMemo<CmdSimpleSelectOption[]>(() => {
    const customers: CmdSimpleSelectOption[] = [];
    if (initialFormData?.customerId) {
      initialFormData.customerId.forEach((customer: CmdSimpleSelectOption) => {
        if (
          PermissionsService.hasAccess(
            PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
            ['create'],
            Number(customer.value)
          )
        ) {
          customers.push(customer);
        }
      });
    }
    return customers;
  }, [initialFormData]);

  const defaultValues = React.useMemo<OnlineTemplateFormValues>(() => {
    const values: OnlineTemplateFormValues = {
      name: props.id && onlinePanelData?.template.name ? onlinePanelData.template.name : '',
      type: props.id && onlinePanelData?.template.type ? onlinePanelData.template.type : '',
      customerId:
        props.id && onlinePanelData?.template.customerId
          ? String(onlinePanelData.template.customerId)
          : !props.id && enabledCustomers && enabledCustomers.length === 1
            ? enabledCustomers[0]?.value
            : '',
      active: props.id && !onlinePanelData?.template.active ? '0' : '1',
      note: props.id && onlinePanelData?.template.note ? onlinePanelData.template.note : '',
      cards:
        props.id && onlinePanelData?.template.cards
          ? onlinePanelData.template.cards.map((card: CardItem) => ({
              id: card.id,
              name: card.name,
              widgets: card.widgets,
            }))
          : [
              {
                id: null,
                name: f({
                  id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.newTab.name',
                }),
                widgets: [],
              },
            ],
      size: {
        columns:
          props.id && onlinePanelData?.template.columnSize
            ? onlinePanelData.template.columnSize
            : 0,
        rows: props.id && onlinePanelData?.template.rowSize ? onlinePanelData.template.rowSize : 0,
      },
    };
    return values;
  }, [
    enabledCustomers,
    f,
    onlinePanelData?.template.active,
    onlinePanelData?.template.cards,
    onlinePanelData?.template.columnSize,
    onlinePanelData?.template.customerId,
    onlinePanelData?.template.name,
    onlinePanelData?.template.note,
    onlinePanelData?.template.rowSize,
    onlinePanelData?.template.type,
    props.id,
  ]);

  const {
    handleSubmit,
    setError,
    control,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<OnlineTemplateFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: '',
      customerId: '',
      active: '0',
      note: '',
      cards: [
        {
          id: null,
          name: f({
            id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.newTab.name',
          }),
          widgets: [],
        },
      ],
      size: {
        columns: 0,
        rows: 0,
      },
    },
    values: defaultValues,
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  React.useMemo(() => {
    if (!props.id && enabledCustomers.length === 1) {
      setValue('customerId', enabledCustomers[0]?.value);
    }
  }, [enabledCustomers, props.id, setValue]);

  // Load data on mount
  // React.useEffect(() => {
  //   const loadAndSetData = async () => {
  //     setIsLoading(true);
  //     // const validationData = await OnlineTemplateService.getValidation();
  //     // const response = await OnlineTemplateService.getInitialFormData();
  //     // let data: IGetTemplateApiResponse | null = null;
  //     // if (props.id) {
  //     //   data = await OnlineTemplateService.getData(props.id);
  //     // }

  //     if (
  //       initialFormData &&
  //       initialFormData.customerId &&
  //       initialFormData.grid &&
  //       validationData &&
  //       initialFormData.type
  //     ) {
  //       // Handle customers by permissions
  //       // const enabledCustomers: CmdSimpleSelectOption[] = [];
  //       // setAllCustomers(initialFormData.customerId);
  //       // initialFormData.customerId.forEach((customer: CmdSimpleSelectOption) => {
  //       //   if (
  //       //     PermissionsService.hasAccess(
  //       //       PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  //       //       ['create'],
  //       //       Number(customer.value)
  //       //     )
  //       //   ) {
  //       //     enabledCustomers.push(customer);
  //       //   }
  //       // });
  //       // console.log('Customers: ', response.customerId, enabledCustomers);
  //       setIsLoading(false);
  //       // setOnlineTemplateCustomers(enabledCustomers);
  //       // if (!props.id && enabledCustomers.length === 1) {
  //       //   setValue('customerId', enabledCustomers[0]?.value);
  //       // }
  //       // Handle template types by permissions
  //       // const enabledTemplateTypes: CmdSimpleSelectOption[] = [];
  //       // initialFormData.type.forEach((templateType: CmdSimpleSelectOption) => {
  //       //   if (
  //       //     templateType.value === 'system' &&
  //       //     PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM, ['create'])
  //       //   ) {
  //       //     enabledTemplateTypes.push(templateType);
  //       //   }
  //       //   if (
  //       //     templateType.value === 'public' &&
  //       //     PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER, ['create'])
  //       //   ) {
  //       //     enabledTemplateTypes.push(templateType);
  //       //   }
  //       //   if (
  //       //     templateType.value === 'private' &&
  //       //     PermissionsService.hasAccess(PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE, ['create'])
  //       //   ) {
  //       //     enabledTemplateTypes.push(templateType);
  //       //   }
  //       // });

  //       // setOnlineTemplateTypes(enabledTemplateTypes);
  //       // setOnlineTemplateValidation(validationData);
  //       // if (props.id && data) {
  //       //   setValue('name', data.template.name);
  //       //   setValue('note', data.template.note);
  //       //   setValue('active', data.template.active ? '1' : '0');
  //       //   setValue('customerId', data.template.customerId ? String(data.template.customerId) : '');
  //       //   setValue('type', data.template.type);
  //       //   setValue('size', {
  //       //     columns: data.template.columnSize,
  //       //     rows: data.template.rowSize,
  //       //   });
  //       //   setValue(
  //       //     'cards',
  //       //     data.template.cards.map((card: ICardItem) => ({
  //       //       id: card.id,
  //       //       name: card.name,
  //       //       widgets: card.widgets,
  //       //     }))
  //       //   );
  //       // }
  //     }
  //   };
  //   // const loadAndSetWidgetCategories = async () => {
  //   //   const widgetCategoriesData = await OnlineTemplateService.getWidgetCategories();
  //   //   if (widgetCategoriesData) {
  //   //     setOnlineTemplateWidgetCategories(widgetCategoriesData as IWidgetCategory[]);
  //   //   }
  //   // };
  //   // const loadAndSetWidgetTypes = async () => {
  //   //   const widgetTypesData = await OnlineTemplateService.getWidgetTypes();
  //   //   if (widgetTypesData) {
  //   //     setOnlineTemplateWidgetTypes(widgetTypesData);
  //   //   }
  //   // };
  //   // const loadAndSetWidgets = async () => {
  //   //   const widgets = await WidgetService.getWidgets();
  //   //   if (widgets) {
  //   //     setOnlineTemplateWidgets(widgets as IWidget[]);
  //   //   }
  //   // };
  //   loadAndSetData();
  //   // loadAndSetWidgetCategories();
  //   // loadAndSetWidgetTypes();
  //   // loadAndSetWidgets();
  // }, [props.id]);

  // When selected customers changes, reload data for customer select
  // React.useEffect(() => {
  //   const enabledCustomers: CmdSimpleSelectOption[] = [];
  //   allCustomers.forEach((customer: CmdSimpleSelectOption) => {
  //     if (
  //       PermissionsService.hasAccess(
  //         PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  //         ['create'],
  //         Number(customer.value)
  //       )
  //     ) {
  //       if (selectedCustomers.includes(Number(customer.value))) {
  //         enabledCustomers.push(customer);
  //       }
  //     }
  //   });
  //   setOnlineTemplateCustomers(enabledCustomers);
  //   if (!props.id && enabledCustomers.length === 1) {
  //     setValue('customerId', enabledCustomers[0]?.value);
  //   }
  // }, [allCustomers, props.id, selectedCustomers]);

  const onSubmit = async (values): Promise<boolean> => {
    setIsSubmitBtnClicked(false);
    const widgetsInLocalStorage = loadWidgetsFromLocalStorage();

    const newCards = values.cards.map((card: CardItem, index: number) => {
      if (widgetsInLocalStorage && widgetsInLocalStorage[index]) {
        const newCard = {
          ...card,
          widgets: widgetsInLocalStorage[index],
        };
        return newCard;
      }
      return card;
    });

    const gridSize = getTemplateSizeByCards(newCards);
    setIsLoading(true);
    const data: OnlineTemplateData = {
      name: values.name,
      type: values.type,
      customerId: values.customerId ? Number(values.customerId) : null,
      columnSize: gridSize.columns,
      rowSize: gridSize.rows,
      active: values.active === '1',
      cards: newCards,
      note: values.note,
    };
    if (props.id) {
      data.id = Number(props.id);
    }
    const response = props.id ? await updateTemplate(props.id, data) : await createTemplate(data);
    setIsLoading(false);
    if (response) {
      if (response.passed === false) {
        if (response.errors.cards) {
          Object.keys(response.errors.cards).forEach((index: string) => {
            const field = response.errors.cards[Number(index)].form;
            field.forEach((item: { [key: string]: string[] }) => {
              Object.keys(item).forEach((fieldName: string) => {
                // setError(`cards.${Number(index) - 1}.${fieldName}`, {
                setError(`cards.${Number(index) - 1}.name`, {
                  message: item[fieldName].join('\n'),
                });
              });
            });
          });
        }
        return false;
      }
      removeWidgetsFromLocalStorage();
      props.reloadTableCallback();
      props.closeCallback('submit');
      return true;
    }
    return false;
  };

  const buttons: CmdSubpageButton[] = [
    {
      id: 'template-close-button',
      title: f({ id: 'form.cancel' }),
      type: 'button',
      className: '',
      closeCallback: props.closeCallback,
    },
  ];

  if (access.canCreate || access.canUpdate) {
    buttons.push({
      id: 'template-submit-button',
      title: f({ id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.save' }),
      type: 'button',
      className: '',
      submitCallback: handleSubmit(onSubmit),
    });
  }

  const handleIsSubmitBtnClicked = (value: boolean) => {
    setIsSubmitBtnClicked(value);
  };

  const handleShowWidgetCategories = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['Online template ', 'click', 'Show widget categories']);
    setActiveWidgetCategory('');
    setActiveWidgetType('');
  };

  const handleShowWidgetTypes = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['Online template ', 'click', 'Show widget types']);
    setActiveWidgetType('');
  };

  const handleChangeTemplateType = (value: string) => {
    if (validationData) {
      if (value === IS_PUBLIC) {
        const newCustomerIdValidation = validationData.customerId;
        const requiredRule: IValidationRule = {
          param: 'required',
          value: true,
          message: f({ id: 'validationRule.required' }),
        };
        newCustomerIdValidation.push(requiredRule);
        const newOnliteTemplateValidation: IValidationApiResponse = {
          ...validationData,
          customerId: newCustomerIdValidation,
        };
        // setOnlineTemplateValidation(newOnliteTemplateValidation);
      } else {
        const newCustomerIdValidation = validationData.customerId.filter(
          (rule: IValidationRule) => rule.param !== 'required'
        );
        const newOnliteTemplateValidation: IValidationApiResponse = {
          ...validationData,
          customerId: newCustomerIdValidation,
        };
        // setOnlineTemplateValidation(newOnliteTemplateValidation);
        setError('customerId', { message: '' });
      }
    }
  };

  const availableWidgets = getAvailableWidgetsByType(
    widgetsData,
    activeWidgetType,
    watch('cards'),
    false
  );

  return (
    <CmdSubpage
      id="online-panel-form-subpage"
      title={f({ id: 'menu.onlinePanel' })}
      subtitle={
        props.id
          ? f({ id: 'onlineTemplates.subPage.edit.subtitle' })
          : f({ id: 'onlineTemplates.subPage.add.subtitle' })
      }
      buttons={buttons}
      loader={
        isLoading ||
        isLoadingValidationData ||
        isLoadingInitialFormData ||
        isLoadingWidgetCategoriesData ||
        isLoadingWidgetTypesData ||
        isLoadingWidgetsData
      }
      maintitle={f({ id: 'menu.settings' })}
      // onSubmitClick={handleIsSubmitBtnClicked}
    >
      <Section
        id="online-template-settings"
        title={f({ id: 'onlineTemplates.subPage.section.templateSettings.title' })}
      >
        <div className="row">
          <div className="col-12 col-lg-6">
            <div className="row">
              <div className="col-12 col-lg-6 col-xl mb-4 mb-xl-6">
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: errors.name ? errors.name.message : undefined }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      type="text"
                      id="name"
                      name="name"
                      label={f({
                        id: 'onlineTemplates.subPage.section.templateSettings.form.name',
                      })}
                      placeholder={f({
                        id: 'onlineTemplates.subPage.section.templateSettings.form.name.placeholder',
                      })}
                      // labelClassName="col-auto form-label form-label-md"
                      disabled={access.isReadOnly || false}
                      required={
                        validationData
                          ? FormikValidatorService.isFieldRequired(validationData.name)
                          : false
                      }
                      onChangeCallback={(value) => {
                        setValue('name', value);
                        trigger('name');
                      }}
                      touched
                      error={errors.name?.message}
                    />
                  )}
                />
              </div>
              <div className="col-12 col-lg-6 col-xl-auto mb-4 mb-xl-6">
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      id="active"
                      label={f({
                        id: 'onlineTemplates.subPage.section.templateSettings.form.active',
                      })}
                      tooltip={f({
                        id: 'onlineTemplates.subPage.section.templateSettings.form.active.tooltip',
                      })}
                      value={watch('active')}
                      disabled={access.isReadOnly || false}
                      setValue={(value) => {
                        if (value === '1') {
                          trackEvent(['Online template ', 'click', 'Change status to active']);
                        }
                        if (value === '0') {
                          trackEvent(['Online template ', 'change', 'Change status to inactive']);
                        }
                        setValue('active', value);
                        trigger('active');
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-xxl-6 mb-4">
                {props.id ? (
                  <CmdLabel
                    name="type"
                    title={f({
                      id: 'onlineTemplates.subPage.section.templateSettings.form.type',
                    })}
                    value={enabledTemplateTypes.find((item) => item.value === watch('type'))?.item}
                    areLabels={true}
                  />
                ) : (
                  <Controller
                    name="type"
                    control={control}
                    rules={{
                      required: errors.type ? errors.type.message : undefined,
                    }}
                    render={({ field }) => (
                      <CmdSimpleSelect
                        {...field}
                        id="type"
                        name="type"
                        label={f({
                          id: 'onlineTemplates.subPage.section.templateSettings.form.type',
                        })}
                        options={enabledTemplateTypes}
                        introOption={f({
                          id: 'onlineTemplates.subPage.section.templateSettings.form.type.introOption',
                        })}
                        required={
                          validationData
                            ? FormikValidatorService.isFieldRequired(validationData.type)
                            : false
                        }
                        disabled={access.isReadOnly || false}
                        onChangeCallback={(value) => {
                          trackEvent(['Online template ', 'change', `Change template type: ${value}`]);
                          setValue('type', value);
                          trigger('type');
                          handleChangeTemplateType(value);
                        }}
                        touched
                        error={errors.type?.message}
                      />
                    )}
                  />
                )}
              </div>

              <div className="col-12 col-xxl-6 mb-4">
                {watch('type') === IS_PUBLIC &&
                  (props.id || enabledCustomers.length === 1 ? (
                    <CmdLabel
                      // id="type"
                      name="customerId"
                      title={f({
                        id: 'onlineTemplates.subPage.section.templateSettings.form.customer',
                      })}
                      value={
                        !props.id && enabledCustomers.length === 1
                          ? enabledCustomers[0]?.item
                          : enabledCustomers.find(
                              (item) => String(item.value) === watch('customerId')
                            )?.item
                      }
                      areLabels={true}
                    />
                  ) : (
                    <Controller
                      name="customerId"
                      control={control}
                      rules={{
                        required: errors.type ? errors.type.message : undefined,
                      }}
                      render={({ field }) => (
                        <CmdSimpleSelect
                          {...field}
                          id="customerId"
                          name="customerId"
                          label={f({
                            id: 'onlineTemplates.subPage.section.templateSettings.form.customer',
                          })}
                          options={enabledCustomers}
                          required={
                            validationData
                              ? FormikValidatorService.isFieldRequired(validationData.customerId)
                              : false
                          }
                          disabled={
                            ((!!access.canCreate || !!access.canUpdate) &&
                              watch('type') !== IS_PUBLIC) ||
                            !!props.id
                          }
                          introOption={f({
                            id: 'onlineTemplates.subPage.section.templateSettings.form.customer.introOption',
                          })}
                          onChangeCallback={(value: string) => {
                            trackEvent(['Online template ', 'change', 'Change customer', Number(value)]);
                            setValue('customerId', value);
                            trigger('customerId');
                          }}
                          touched
                          error={errors.customerId?.message}
                        />
                      )}
                    />
                  ))}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg mb-4">
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <CmdTextarea
                  {...field}
                  id="note"
                  name="note"
                  containerClass="form-group h-100"
                  // className="form-control h-100"
                  label={f({ id: 'onlineTemplates.subPage.section.templateSettings.form.notes' })}
                  rows={5}
                  disabled={access.isReadOnly || false}
                  required={
                    validationData
                      ? FormikValidatorService.isFieldRequired(validationData.note)
                      : false
                  }
                  value={watch('note')}
                  setValue={(value) => {
                    setValue('note', value);
                    trigger();
                  }}
                />
              )}
            />
          </div>
        </div>
      </Section>
      {showGridAndWidgets(getValues()) && (
        <>
          {!activeWidgetCategory && !activeWidgetType && (access.canCreate || access.canUpdate) && (
            <Section
              id="widget-categories"
              title={f({ id: 'onlineTemplates.subPage.section.widgetCategories.title' })}
            >
              <p className="basic-box__infotext">
                {f({ id: 'onlineTemplates.subPage.section.widgetCategories.infoText' })}
              </p>
              {widgetCategoriesData &&
                widgetCategoriesData.map((onlineWidgetCategory: IWidgetCategory, index: number) => (
                  <WidgetCategory
                    key={`widget-category-${onlineWidgetCategory.value}-${index}`}
                    value={onlineWidgetCategory.value}
                    title={onlineWidgetCategory.item}
                    active={onlineWidgetCategory.value === activeWidgetCategory}
                    handleSelectWidgetCategory={setActiveWidgetCategory}
                  />
                ))}
            </Section>
          )}
          {activeWidgetCategory &&
            widgetCategoriesData &&
            !activeWidgetType &&
            (access.canCreate || access.canUpdate) && (
              <section className="basic-box">
                <header className="basic-box__header">
                  <div style={{ display: 'inline-flex' }}>
                    <a
                      href="#"
                      className="cmd-icon cmd-icon-arrow-left-ext"
                      onClick={handleShowWidgetCategories}
                    />
                    {getWidgetCategoryNameById(widgetCategoriesData, activeWidgetCategory)}
                  </div>
                </header>
                <div className="basic-box__content">
                  <p className="basic-box__infotext">
                    {f({ id: 'onlineTemplates.subPage.section.widgetCategories.infoText' })}
                  </p>
                  {widgetsData &&
                    widgetTypesData &&
                    getWidgetTypesByCategory(
                      widgetsData,
                      widgetTypesData,
                      activeWidgetCategory
                    ).map((widgetType: IWidgetType, index: number) => (
                      <WidgetType
                        key={`widget-type-${widgetType.value}-${index}`}
                        value={widgetType.value}
                        title={widgetType.item}
                        active={widgetType.value === activeWidgetType}
                        handleSelectWidgetType={setActiveWidgetType}
                      />
                    ))}
                </div>
              </section>
            )}
          {activeWidgetCategory &&
            activeWidgetType &&
            widgetsData &&
            widgetsData.length > 0 &&
            (access.canCreate || access.canUpdate) && (
              <section className="basic-box">
                <header className="basic-box__header">
                  <div style={{ display: 'inline-flex' }}>
                    <a
                      href="#"
                      className="cmd-icon cmd-icon-arrow-left-ext"
                      onClick={handleShowWidgetTypes}
                    />
                    {widgetTypesData && getWidgetTypeNameById(widgetTypesData, activeWidgetType)}
                  </div>
                </header>
                <div className="basic-box__content">
                  <p className="basic-box__infotext">
                    {f({ id: 'onlineTemplates.subPage.section.widgetCategories.infoText2' })}
                  </p>
                  <div className="d-flex flex-wrap">
                    {(availableWidgets as IWidget[]).map((widget: IWidget, index: number) => (
                      <Widget
                        key={`${index}-${widget.gridName}`}
                        index={index}
                        data={widget}
                        setDraggedWidget={setDraggedWidget}
                        setDroppingWidget={setDroppingWidget}
                        isMockupMode={true}
                        isInGrid={false}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}
        </>
      )}
      {showGridAndWidgets(getValues()) && widgetsData && (
        <CardsSection
          droppingWidget={droppingWidget}
          draggedWidget={draggedWidget}
          setDraggedWidget={setDraggedWidget}
          setDroppingWidget={setDroppingWidget}
          widgets={widgetsData}
          // value={watch('cards')}
          value={getValues('cards')}
          error={errors.cards as CardError[]}
          setValue={setValue}
          control={control}
          isSubmitBtnClicked={isSubmitBtnClicked}
          resetSubmitBtnClicked={handleIsSubmitBtnClicked}
        />
      )}
    </CmdSubpage>
  );
}
